import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Link, Button } from '@mui/material';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { apiService } from '../../services/apiService';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../context/AuthContext';

const IssueDetail: React.FC = () => {
  const { initialized, authenticated, login } = useAuth();
  const [authRequired, setAuthRequired] = useState(false);
  const { slug } = useParams<{ slug: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [issue, setIssue] = useState<{ slug: string; title: string } | null>(null);
  const [article, setArticle] = useState<string>("");
  const [references, setReferences] = useState<Array<{ id: string; title: string }>>([]);
  const [metaDescription, setMetaDescription] = useState<string>("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!slug || !initialized) return;
      setLoading(true);
      setError(null);
      try {
        const res = await apiService.getIssueArticle(slug);
        if (!mounted) return;
        const data = res.data || {};
        setAuthRequired(!!data.auth_required);
        setIssue(data.issue || null);
        const rawArticle = typeof data.article === 'string' ? data.article : '';
        // Extract and remove Meta Description line if present
        const mdMatch = rawArticle.match(/(^|\n)\s*Meta Description:\s*(.+)\s*$/i);
        if (mdMatch && mdMatch[2]) {
          setMetaDescription(mdMatch[2].trim());
        } else {
          setMetaDescription("");
        }
        const cleaned = rawArticle.replace(/\n?\s*Meta Description:\s*.+$/i, '').trim();
        setArticle(cleaned);
        setReferences(Array.isArray(data.references) ? data.references : []);
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Failed to load issue');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [slug, initialized, authenticated]);

  // Update document title and meta description for SEO
  useEffect(() => {
    if (issue?.title) {
      document.title = `${issue.title} – AskTheBill.ai`;
    }
    // Manage <meta name="description"> tag
    const name = 'description';
    let tag = document.querySelector(`meta[name="${name}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', name);
      document.head.appendChild(tag);
    }
    if (tag) {
      tag.setAttribute('content', metaDescription || (issue?.title ? `${issue.title} legislative update and analysis.` : '')); 
    }
  }, [issue?.title, metaDescription]);

  if (loading) return <LoadingSpinner message="Loading issue..." />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!issue) return <Typography color="textSecondary">Issue not found.</Typography>;

  const components = {
    h1: ({ children }: any) => (
      <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
        {children}
      </Typography>
    ),
    h2: ({ children }: any) => (
      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
        {children}
      </Typography>
    ),
    h3: ({ children }: any) => (
      <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2, fontWeight: 600 }}>
        {children}
      </Typography>
    ),
    p: ({ children }: any) => (
      <Typography variant="body1" paragraph sx={{ lineHeight: 1.75 }}>
        {children}
      </Typography>
    ),
    a: ({ href, children }: any) => (
      <Link href={href} underline="hover" target="_blank" rel="noopener noreferrer">
        {children}
      </Link>
    ),
    em: ({ children }: any) => <em>{children}</em>,
    strong: ({ children }: any) => <strong>{children}</strong>,
    ul: ({ children }: any) => (
      <Box component="ul" sx={{ pl: 3, my: 1 }}>
        {children}
      </Box>
    ),
    li: ({ children }: any) => (
      <Box component="li" sx={{ mb: 0.5 }}>
        <Typography variant="body1" component="span">{children}</Typography>
      </Box>
    ),
    blockquote: ({ children }: any) => (
      <Box component="blockquote" sx={{ borderLeft: '4px solid', borderColor: 'divider', pl: 2, my: 2, color: 'text.secondary' }}>
        {children}
      </Box>
    )
  } as any;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {issue.title}
      </Typography>

      <Card variant="outlined">
        <CardContent>
          {article ? (
            <Box className="issue-article" sx={{
              '& h1, & h2, & h3': { scrollMarginTop: 80 },
              '& p': { mb: 2 },
              '& a': { color: 'primary.main' },
              maxWidth: 900,
              mx: 'auto'
            }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {article}
              </ReactMarkdown>
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              {authRequired ? <>Sign in to generate an AI article about this issue. <Button onClick={login}>Sign in</Button></> : 'No article available for this issue yet.'}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Related Bills
        </Typography>
        {references && references.length > 0 ? (
          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            {references.map((ref) => {
              const [congress, billType, billNumber] = (ref.id || '').split('/');
              const to = congress && billType && billNumber ? `/bills/${congress}/${billType}/${billNumber}` : undefined;
              return (
                <li key={ref.id}>
                  {to ? (
                    <Link component={RouterLink} to={to} underline="hover">
                      {ref.title || ref.id}
                    </Link>
                  ) : (
                    <Typography variant="body2">{ref.title || ref.id}</Typography>
                  )}
                </li>
              );
            })}
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary">No related bills found.</Typography>
        )}
      </Box>
    </Box>
  );
};

export default IssueDetail;
